import { encryptionService } from './encryptionService';
import { dataBackupService } from './dataBackupService';
import { onboardingService } from './onboardingService';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  emailVerified: boolean;
  createdAt: Date;
  lastLoginAt: Date;
  subscription?: {
    plan: 'free' | 'pro' | 'enterprise' | 'pioneer';
    status: 'active' | 'trialing' | 'cancelled' | 'past_due' | 'inactive';
    expiresAt?: Date;
    stripeCustomerId?: string;
    isPioneer?: boolean;
    pioneerNumber?: number;
  };
  preferences: {
    enableCloudSync: boolean;
    syncInterval: 'realtime' | 'hourly' | 'daily' | 'manual';
    encryptCloudData: boolean;
    twoFactorEnabled: boolean;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpiresAt: Date | null;
}

class AuthService {
  private readonly API_BASE = import.meta.env.VITE_API_URL || 'https://api.godmode.app';
  private readonly AUTH_STORAGE_KEY = 'godmode_auth';
  private readonly TOKEN_STORAGE_KEY = 'godmode_auth_token';
  private readonly REFRESH_TOKEN_STORAGE_KEY = 'godmode_auth_refresh_token';
  private authState: AuthState = {
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
    sessionExpiresAt: null
  };
  private refreshTimer: NodeJS.Timeout | null = null;
  private syncTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeAuth();
  }

  // Initialize authentication state from localStorage
  private initializeAuth(): void {
    const storedAuth = localStorage.getItem(this.AUTH_STORAGE_KEY);
    const storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    const storedRefreshToken = localStorage.getItem(this.REFRESH_TOKEN_STORAGE_KEY);

    if (storedAuth && storedToken) {
      try {
        const authData = JSON.parse(storedAuth);
        this.authState = {
          ...authData,
          token: storedToken,
          refreshToken: storedRefreshToken,
          isAuthenticated: true,
          isLoading: false
        };

        // Verify token is still valid
        this.verifySession();

        // Setup auto-refresh
        this.setupTokenRefresh();

        // Setup cloud sync if enabled
        if (authData.user?.preferences?.enableCloudSync) {
          this.setupCloudSync();
        }
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        this.clearAuth();
      }
    }
  }

  // Sign up new user
  async signUp(email: string, password: string, name: string): Promise<User> {
    try {
      const response = await fetch(`${this.API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData.message || errorData.error || 'Sign up failed';
        
        // Friendly error messages
        if (response.status === 409 || message.toLowerCase().includes('already exists')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw new Error(message);
      }

      const data = await response.json();

      // Store auth data
      this.setAuthState(data.user, data.token, data.refreshToken);

      // Migrate existing localStorage data to user account
      try {
        await this.migrateLocalDataToCloud();
      } catch (migrationError) {
        console.warn('⚠️ Data migration skipped (non-critical):', migrationError);
        // Don't block signup if migration fails - data stays in localStorage
      }

      // Seed onboarding aspiration for new users (non-blocking)
      onboardingService.seedOnboardingData().catch(e =>
        console.warn('Onboarding data seeding skipped:', e)
      );

      // Send welcome email (non-blocking)
      this.sendWelcomeEmail().catch(e => console.warn('Welcome email skipped:', e));

      return data.user;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  // Sign in existing user
  async signIn(email: string, password: string): Promise<User | { requires2FA: true; tempToken: string; email: string }> {
    try {
      const response = await fetch(`${this.API_BASE}/api/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Sign in failed');
      }

      const data = await response.json();

      // Check if 2FA is required
      if (data.requires2FA) {
        return { requires2FA: true, tempToken: data.tempToken, email };
      }

      // Store auth data
      this.setAuthState(data.user, data.token, data.refreshToken);

      // Handle data sync strategy
      await this.handleDataSyncOnSignIn();

      return data.user;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  // Verify 2FA challenge during sign-in
  async verifyTwoFactorChallenge(tempToken: string, code: string, isBackupCode = false): Promise<User> {
    const response = await fetch(`${this.API_BASE}/api/auth/2fa/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, code, isBackupCode })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Verification failed');
    }

    const data = await response.json();
    this.setAuthState(data.user, data.token, data.refreshToken);
    await this.handleDataSyncOnSignIn();
    return data.user;
  }

  // Disable two-factor authentication
  async disableTwoFactor(password: string, code: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/auth/2fa/disable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password, code })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to disable 2FA');
    }

    if (this.authState.user) {
      this.authState.user.preferences.twoFactorEnabled = false;
      this.saveAuthState();
    }
  }

  // Regenerate backup codes
  async regenerateBackupCodes(code: string): Promise<string[]> {
    const response = await fetch(`${this.API_BASE}/api/auth/2fa/backup-codes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to regenerate backup codes');
    }

    const data = await response.json();
    return data.backupCodes;
  }

  // OAuth sign in (Google, Microsoft, etc.)
  async signInWithOAuth(provider: 'google' | 'microsoft'): Promise<void> {
    const authUrl = `${this.API_BASE}/api/auth/oauth-${provider}`;
    const redirectUrl = `${window.location.origin}/auth/callback`;

    // Use redirect-based OAuth (not popup)
    // The AuthCallback page will handle the rest of the flow
    window.location.href = `${authUrl}?redirect_uri=${encodeURIComponent(redirectUrl)}`;

    // This promise will never resolve in this page since we're redirecting
    // The AuthCallback page will handle the authentication completion
    return new Promise(() => {
      // This promise intentionally never resolves since we're redirecting away
    });
  }

  // Sign out user
  async signOut(): Promise<void> {
    try {
      // Sync any pending changes before sign out
      if (this.authState.user?.preferences?.enableCloudSync) {
        await this.syncToCloud();
      }

      // Call server to invalidate tokens
      if (this.authState.token) {
        await fetch(`${this.API_BASE}/api/auth/signout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.authState.token}`
          }
        });
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      this.clearAuth();
    }
  }

  // Verify email address
  async verifyEmail(verificationToken: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verificationToken })
    });

    if (!response.ok) {
      throw new Error('Email verification failed');
    }

    // Update user's email verification status
    if (this.authState.user) {
      this.authState.user.emailVerified = true;
      this.saveAuthState();
    }
  }

  // Resend verification email
  async resendVerificationEmail(): Promise<void> {
    const makeRequest = () => fetch(`${this.API_BASE}/api/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json',
      },
    });

    const userEmail = this.authState.user?.email;
    let response = await makeRequest();

    // If token expired, try refreshing and retry once
    if (response.status === 401 && this.authState.refreshToken) {
      await this.refreshToken();
      response = await makeRequest();
    }

    // If still unauthorized, fall back to email-based resend
    if (response.status === 401 && userEmail) {
      return this.resendVerificationByEmail(userEmail);
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to resend verification email');
    }
  }

  // Resend verification email by email address (no auth required)
  async resendVerificationByEmail(email: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/auth/resend-verification-by-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to resend verification email');
    }
  }

  // Password reset request
  async requestPasswordReset(email: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      throw new Error('Password reset request failed');
    }
  }

  // Reset password with token
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/auth/reset-password/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password: newPassword })
    });

    if (!response.ok) {
      throw new Error('Password reset failed');
    }
  }

  // Change password (authenticated)
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    let token = this.authState.token;

    // If token is missing, try refreshing first
    if (!token && this.authState.refreshToken) {
      await this.refreshToken();
      token = this.authState.token;
    }

    if (!token) throw new Error('Not authenticated. Please sign in again.');

    const response = await fetch(`${this.API_BASE}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to change password');
    }
  }

  // Two-factor authentication setup
  async setupTwoFactor(): Promise<{ qrCode: string; secret: string }> {
    const response = await fetch(`${this.API_BASE}/api/auth/2fa/setup`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`
      }
    });

    if (!response.ok) {
      throw new Error('2FA setup failed');
    }

    return await response.json();
  }

  // Verify two-factor code to complete setup
  async verifyTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
    const response = await fetch(`${this.API_BASE}/api/auth/2fa/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Invalid 2FA code');
    }

    const data = await response.json();

    // Update user preferences
    if (this.authState.user) {
      this.authState.user.preferences.twoFactorEnabled = true;
      this.saveAuthState();
    }

    return { backupCodes: data.backupCodes };
  }

  // Migrate local data to cloud on first sign up
  private async migrateLocalDataToCloud(): Promise<void> {
    if (!this.authState.token) return;

    console.log('🔄 Migrating local data to cloud...');

    // Create backup of current local data
    const backupData = dataBackupService.createBackupData();

    // Encrypt if user preference is set
    let dataToUpload = JSON.stringify(backupData);
    if (this.authState.user?.preferences?.encryptCloudData) {
      const encryptionKey = await this.deriveEncryptionKey();
      const encryptionResult = await encryptionService.encryptData(dataToUpload, encryptionKey);
      dataToUpload = JSON.stringify(encryptionResult);
    }

    // Upload to server
    const response = await fetch(`${this.API_BASE}/sync/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: dataToUpload,
        encrypted: this.authState.user?.preferences?.encryptCloudData || false,
        timestamp: new Date().toISOString(),
        deviceInfo: this.getDeviceInfo()
      })
    });

    if (!response.ok) {
      throw new Error('Failed to migrate data to cloud');
    }

    console.log('✅ Local data migrated to cloud successfully');
  }

  // Handle data sync strategy on sign in
  private async handleDataSyncOnSignIn(): Promise<void> {
    const hasLocalData = this.checkForLocalData();
    const hasCloudData = await this.checkForCloudData();

    if (hasLocalData && hasCloudData) {
      // Conflict resolution: ask user what to do
      const strategy = await this.promptSyncStrategy();

      switch (strategy) {
        case 'merge':
          await this.mergeLocalAndCloudData();
          break;
        case 'cloud':
          await this.downloadFromCloud();
          break;
        case 'local':
          await this.uploadToCloud();
          break;
        case 'keep-both':
          // Keep local data as-is, cloud data remains separate
          break;
      }
    } else if (hasCloudData && !hasLocalData) {
      // Download cloud data
      await this.downloadFromCloud();
    } else if (hasLocalData && !hasCloudData) {
      // Upload local data
      await this.uploadToCloud();
    }
  }

  // Check if local data exists
  private checkForLocalData(): boolean {
    const keys = ['godmode_aspirations', 'godmode_goals', 'godmode_lists'];
    return keys.some(key => {
      const data = localStorage.getItem(key);
      return data && JSON.parse(data).length > 0;
    });
  }

  // Check if cloud data exists
  private async checkForCloudData(): Promise<boolean> {
    if (!this.authState.token) return false;

    try {
      const response = await fetch(`${this.API_BASE}/sync/status`, {
        headers: {
          'Authorization': `Bearer ${this.authState.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.hasData;
      }
    } catch (error) {
      console.error('Failed to check cloud data:', error);
    }

    return false;
  }

  // Download data from cloud
  private async downloadFromCloud(): Promise<void> {
    if (!this.authState.token) return;

    const response = await fetch(`${this.API_BASE}/sync/download`, {
      headers: {
        'Authorization': `Bearer ${this.authState.token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download cloud data');
    }

    const cloudData = await response.json();

    // Decrypt if needed
    let dataToRestore = cloudData.data;
    if (cloudData.encrypted && this.authState.user?.preferences?.encryptCloudData) {
      const encryptionKey = await this.deriveEncryptionKey();
      const encryptionResult = JSON.parse(dataToRestore);
      dataToRestore = await encryptionService.decryptData({
        encryptedData: encryptionResult.encryptedData,
        password: encryptionKey,
        salt: encryptionResult.salt,
        iv: encryptionResult.iv
      });
    }

    // Restore to localStorage
    const backupData = JSON.parse(dataToRestore);
    await dataBackupService.restoreFromBackup(backupData);

    console.log('✅ Cloud data downloaded successfully');
  }

  // Upload local data to cloud
  private async uploadToCloud(): Promise<void> {
    await this.syncToCloud();
  }

  // Sync local changes to cloud
  async syncToCloud(): Promise<void> {
    if (!this.authState.token) return;
    if (!this.authState.user?.preferences?.enableCloudSync) return;

    console.log('🔄 Syncing to cloud...');

    const backupData = dataBackupService.createBackupData();

    // Encrypt if preference is set
    let dataToUpload = JSON.stringify(backupData);
    if (this.authState.user?.preferences?.encryptCloudData) {
      const encryptionKey = await this.deriveEncryptionKey();
      const encryptionResult = await encryptionService.encryptData(dataToUpload, encryptionKey);
      dataToUpload = JSON.stringify(encryptionResult);
    }

    const response = await fetch(`${this.API_BASE}/sync/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: dataToUpload,
        encrypted: this.authState.user?.preferences?.encryptCloudData || false,
        timestamp: new Date().toISOString(),
        deviceInfo: this.getDeviceInfo()
      })
    });

    if (!response.ok) {
      throw new Error('Failed to sync to cloud');
    }

    console.log('✅ Synced to cloud successfully');
  }

  // Setup automatic cloud sync
  private setupCloudSync(): void {
    if (!this.authState.user?.preferences?.enableCloudSync) return;

    // Clear existing timer
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }

    const syncInterval = this.authState.user.preferences.syncInterval;
    let intervalMs: number;

    switch (syncInterval) {
      case 'realtime':
        // Listen for localStorage changes
        this.setupRealtimeSync();
        return;
      case 'hourly':
        intervalMs = 60 * 60 * 1000;
        break;
      case 'daily':
        intervalMs = 24 * 60 * 60 * 1000;
        break;
      default:
        return; // Manual sync only
    }

    this.syncTimer = setInterval(() => {
      this.syncToCloud().catch(console.error);
    }, intervalMs);
  }

  // Setup realtime sync with debouncing
  private setupRealtimeSync(): void {
    let syncDebounceTimer: NodeJS.Timeout | null = null;

    // Listen for storage events
    window.addEventListener('storage', (event) => {
      if (event.key?.startsWith('godmode_') && event.key !== this.AUTH_STORAGE_KEY) {
        // Debounce sync calls
        if (syncDebounceTimer) clearTimeout(syncDebounceTimer);
        syncDebounceTimer = setTimeout(() => {
          this.syncToCloud().catch(console.error);
        }, 5000); // Wait 5 seconds after last change
      }
    });
  }

  // Derive encryption key from user password
  private async deriveEncryptionKey(): Promise<string> {
    if (!this.authState.user) throw new Error('No user authenticated');

    // Use a combination of user ID and a secret for the encryption key
    // In production, this should use a proper key derivation function
    return `${this.authState.user.id}-${this.authState.token?.slice(0, 16)}`;
  }

  // Merge local and cloud data intelligently
  private async mergeLocalAndCloudData(): Promise<void> {
    // Download cloud data
    const cloudResponse = await fetch(`${this.API_BASE}/sync/download`, {
      headers: {
        'Authorization': `Bearer ${this.authState.token}`
      }
    });

    if (!cloudResponse.ok) {
      throw new Error('Failed to download cloud data for merge');
    }

    const cloudData = await cloudResponse.json();
    let cloudBackup = JSON.parse(cloudData.data);

    if (cloudData.encrypted) {
      const encryptionKey = await this.deriveEncryptionKey();
      const encryptionResult = JSON.parse(cloudData.data);
      const decrypted = await encryptionService.decryptData({
        encryptedData: encryptionResult.encryptedData,
        password: encryptionKey,
        salt: encryptionResult.salt,
        iv: encryptionResult.iv
      });
      cloudBackup = JSON.parse(decrypted);
    }

    // Get local data
    const localBackup = dataBackupService.createBackupData();

    // Merge strategy: combine both datasets, preferring newer items
    const mergedData = this.mergeBackupData(localBackup, cloudBackup);

    // Restore merged data locally
    await dataBackupService.restoreFromBackup(mergedData);

    // Upload merged data to cloud
    await this.syncToCloud();

    console.log('✅ Local and cloud data merged successfully');
  }

  // Intelligent data merging
  private mergeBackupData(local: any, cloud: any): any {
    const merged = {
      version: local.version,
      timestamp: new Date().toISOString(),
      userProfile: this.mergeByTimestamp(local.userProfile, cloud.userProfile),
      aspirations: this.mergeArraysByIdAndTimestamp(local.aspirations || [], cloud.aspirations || []),
      goals: this.mergeArraysByIdAndTimestamp(local.goals || [], cloud.goals || []),
      lists: this.mergeArraysByIdAndTimestamp(local.lists || [], cloud.lists || []),
      schedules: this.mergeArraysByIdAndTimestamp(local.schedules || [], cloud.schedules || []),
      aiConfig: this.mergeByTimestamp(local.aiConfig, cloud.aiConfig),
      metadata: local.metadata
    };

    return merged;
  }

  // Merge arrays by ID, preferring newer items
  private mergeArraysByIdAndTimestamp(localArray: any[], cloudArray: any[]): any[] {
    const mergedMap = new Map();

    // Add all local items
    localArray.forEach(item => {
      mergedMap.set(item.id, item);
    });

    // Add or update with cloud items if newer
    cloudArray.forEach(cloudItem => {
      const localItem = mergedMap.get(cloudItem.id);
      if (!localItem || new Date(cloudItem.updatedAt) > new Date(localItem.updatedAt)) {
        mergedMap.set(cloudItem.id, cloudItem);
      }
    });

    return Array.from(mergedMap.values());
  }

  // Merge objects by timestamp
  private mergeByTimestamp(local: any, cloud: any): any {
    if (!local) return cloud;
    if (!cloud) return local;

    const localTime = new Date(local.updatedAt || local.timestamp || 0);
    const cloudTime = new Date(cloud.updatedAt || cloud.timestamp || 0);

    return localTime > cloudTime ? local : cloud;
  }

  // Prompt user for sync strategy
  private async promptSyncStrategy(): Promise<'merge' | 'cloud' | 'local' | 'keep-both'> {
    // This would show a modal in the UI
    // For now, default to merge
    return 'merge';
  }

  // Setup token refresh
  private setupTokenRefresh(): void {
    if (!this.authState.refreshToken || !this.authState.sessionExpiresAt) return;

    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiresAt = new Date(this.authState.sessionExpiresAt);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();

    // Refresh 5 minutes before expiry
    const refreshIn = Math.max(0, timeUntilExpiry - 5 * 60 * 1000);

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, refreshIn);
  }

  // Refresh authentication token
  private async refreshToken(): Promise<void> {
    if (!this.authState.refreshToken) return;

    try {
      const response = await fetch(`${this.API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.authState.refreshToken })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      this.authState.token = data.token;
      this.authState.sessionExpiresAt = new Date(data.expiresAt);
      this.saveAuthState();

      // Setup next refresh
      this.setupTokenRefresh();
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuth();
    }
  }

  // Verify current session
  private async verifySession(): Promise<void> {
    if (!this.authState.token) return;

    try {
      const response = await fetch(`${this.API_BASE}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${this.authState.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Session verification failed');
      }

      const data = await response.json();
      this.authState.user = data.user;
      this.authState.sessionExpiresAt = new Date(data.expiresAt);
      this.saveAuthState();
    } catch (error) {
      console.error('Session verification error:', error);
      this.clearAuth();
    }
  }

  // Update user profile
  async updateProfile(updates: Partial<User>): Promise<User> {
    if (!this.authState.token) throw new Error('Not authenticated');

    const response = await fetch(`${this.API_BASE}/api/users/profile`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Profile update failed');
    }

    const updatedUser = await response.json();
    this.authState.user = updatedUser;
    this.saveAuthState();

    return updatedUser;
  }

  // Update sync preferences
  async updateSyncPreferences(preferences: Partial<User['preferences']>): Promise<void> {
    if (!this.authState.user) throw new Error('Not authenticated');

    await this.updateProfile({
      preferences: {
        ...this.authState.user.preferences,
        ...preferences
      }
    });

    // Setup or teardown sync based on new preferences
    if (preferences.enableCloudSync !== undefined) {
      if (preferences.enableCloudSync) {
        this.setupCloudSync();
      } else {
        if (this.syncTimer) {
          clearInterval(this.syncTimer);
          this.syncTimer = null;
        }
      }
    }
  }

  // Delete user account
  async deleteAccount(): Promise<void> {
    if (!this.authState.token) throw new Error('Not authenticated');

    const response = await fetch(`${this.API_BASE}/api/users/account`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.authState.token}`
      }
    });

    if (!response.ok) {
      throw new Error('Account deletion failed');
    }

    this.clearAuth();
  }

  // Helper methods
  private setAuthState(user: User, token: string, refreshToken: string): void {
    this.authState = {
      user,
      token,
      refreshToken,
      isAuthenticated: true,
      isLoading: false,
      sessionExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
    };

    this.saveAuthState();
    this.setupTokenRefresh();

    if (user.preferences.enableCloudSync) {
      this.setupCloudSync();
    }
  }

  private saveAuthState(): void {
    localStorage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify({
      user: this.authState.user,
      sessionExpiresAt: this.authState.sessionExpiresAt
    }));

    if (this.authState.token) {
      localStorage.setItem(this.TOKEN_STORAGE_KEY, this.authState.token);
    }

    if (this.authState.refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_STORAGE_KEY, this.authState.refreshToken);
    }
  }

  private clearAuth(): void {
    this.authState = {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      sessionExpiresAt: null
    };

    localStorage.removeItem(this.AUTH_STORAGE_KEY);
    localStorage.removeItem(this.TOKEN_STORAGE_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_STORAGE_KEY);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private getDeviceInfo(): string {
    return `${navigator.userAgent} - ${navigator.platform}`;
  }

  private async sendWelcomeEmail(): Promise<void> {
    // Welcome email is sent from the server
  }

  // Public getters
  getUser(): User | null {
    return this.authState.user;
  }

  getToken(): string | null {
    return this.authState.token;
  }

  isAuthenticated(): boolean {
    return this.authState.isAuthenticated;
  }

  getAuthState(): AuthState {
    return { ...this.authState };
  }
}

export const authService = new AuthService();